import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { message } from "antd";
import { BaseUrl } from "../../config/BaseUrl";

const BirthdayImages = () => {
  const [records, setRecords] = useState([]);
  const [title, setTitle] = useState("Birthday");
  const [images, setImages] = useState([]);
  const [description, setDescription] = useState("");
  const [recordId, setRecordId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const selectedRecordId = useRef(null);

  const API_BASE = BaseUrl || "";

  const loadBirthdayImages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/v1/birthday/all`);
      const birthdayRecords = Array.isArray(response?.data?.birthdays)
        ? response.data.birthdays
        : [];
      setRecords(birthdayRecords);
      const birthday = birthdayRecords.find((item) => item._id === selectedRecordId.current)
        || (!selectedRecordId.current ? birthdayRecords[0] : null);

      if (birthday) {
        selectedRecordId.current = birthday._id;
        setRecordId(birthday._id);
        setTitle(birthday.title || "Birthday");
        setImages(birthday.birthdayImage || []);
        setDescription(birthday.description || "");
      } else if (recordId) {
        selectedRecordId.current = null;
        setRecordId(null);
        setTitle("Birthday");
        setImages([]);
        setDescription("");
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to load birthday images",
      );
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    loadBirthdayImages();
  }, [loadBirthdayImages]);

  const saveImages = async (nextImages) => {
    try {
      const payload = {
        title: title.trim() || "Birthday",
        description,
        birthdayImage: nextImages,
      };
      let savedBirthday;
      if (recordId) {
        const response = await axios.put(
          `${API_BASE}/api/v1/birthday/update/${recordId}`,
          payload,
        );
        savedBirthday = response?.data?.updatedBirthday || {
          _id: recordId,
          ...payload,
        };
      } else {
        const response = await axios.post(
          `${API_BASE}/api/v1/birthday/new`,
          payload,
        );
        savedBirthday = response?.data?.birthday;
        if (!savedBirthday?._id) throw new Error("Birthday set was not created");
        selectedRecordId.current = savedBirthday._id;
        setRecordId(savedBirthday._id);
      }

      setRecords((current) => {
        const exists = current.some((item) => item._id === savedBirthday._id);
        return exists
          ? current.map((item) => item._id === savedBirthday._id ? savedBirthday : item)
          : [savedBirthday, ...current];
      });
    } catch (error) {
      throw new Error(
        error?.response?.data?.message || "Unable to save birthday images",
      );
    }
  };

  const handleSaveDescription = async () => {
    try {
      if (!recordId) {
        message.warning("Add an image before saving a new birthday set.");
        return;
      }
      await saveImages(images);
      message.success("Birthday description saved");
    } catch (error) {
      message.error(error?.message || "Failed to save birthday description");
    }
  };

  const startNewBirthdaySet = () => {
    selectedRecordId.current = null;
    setRecordId(null);
    setTitle("Birthday");
    setDescription("");
    setImages([]);
    setViewOpen(false);
  };

  const selectBirthdaySet = (birthday) => {
    selectedRecordId.current = birthday._inid;
    setRecordId(birthday._id);
    setTitle(birthday.title || "Birthday");
    setDescription(birthday.description || "");
    setImages(birthday.birthdayImage || []);
    setViewOpen(false);
  };

  const deleteBirthdaySet = async (birthday) => {
    const confirmed = window.confirm(`Delete the birthday set "${birthday.title || "Birthday"}" and all its images?`);
    if (!confirmed) return;

    try {
      await axios.delete(`${API_BASE}/api/v1/birthday/set/${birthday._id}`);
      const remainingRecords = records.filter((item) => item._id !== birthday._id);
      setRecords(remainingRecords);

      if (recordId === birthday._id) {
        const nextRecord = remainingRecords[0];
        selectedRecordId.current = nextRecord?._id || null;
        setRecordId(nextRecord?._id || null);
        setTitle(nextRecord?.title || "Birthday");
        setDescription(nextRecord?.description || "");
        setImages(nextRecord?.birthdayImage || []);
        setViewOpen(false);
      }

      message.success("Birthday set deleted");
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to delete birthday set");
    }
  };

  const handleImageUpload = async (files) => {
    if (!files.length) return;

    try {
      setUploading(true);
      const uploadedUrls = [];
      for (const file of files) {
        const reader = new FileReader();
        const base64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const uploadResponse = await axios.post(
          `${API_BASE}/api/v1/birthday/uploadBirthdayImage`,
          {
            image: base64,
          },
        );
        const uploadedUrl = uploadResponse?.data?.birthdayImg;
        if (!uploadedUrl) throw new Error("No image URL returned from API");
        uploadedUrls.push(uploadedUrl);
      }

      const updatedImages = [...images, ...uploadedUrls];
      await saveImages(updatedImages);
      setImages(updatedImages);
      message.success(
        `${uploadedUrls.length} birthday image${uploadedUrls.length === 1 ? "" : "s"} uploaded`,
      );
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Birthday image upload failed",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (index) => {
    try {
      const nextImages = images.filter((_, itemIndex) => itemIndex !== index);
      await saveImages(nextImages);
      setImages(nextImages);
      message.success("Birthday image removed");
    } catch (error) {
      message.error(error?.message || "Failed to remove birthday image");
    }
  };

  return (
    <div
      className="main_fragment_container p-2"
      style={{ height: "86vh", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", boxSizing: "border-box" }}
    >
      <div className="profile_container_header" style={{ flex: "0 0 10vh" }}>
        <div className="profile_co_header_left">
          <span>Birthday Images</span>
        </div>
      </div>

      <div
        className="main_recha_container"
        style={{
          flex: "1 1 0",
          minHeight: 0,
          padding: 16,
          maxHeight: "none",
          overflowY: "auto",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div>
            <strong>{recordId ? `Managing: ${title}` : "Create birthday image set"}</strong>
            <div style={{ color: "#666", fontSize: 13 }}>
              {records.length} birthday set{records.length === 1 ? "" : "s"}
            </div>
          </div>
          <Button variant="outlined" onClick={startNewBirthdaySet} disabled={uploading || loading}>
            New Birthday Set
          </Button>
        </div>

        <div
          style={{ display: "grid", gap: 16, maxWidth: 850, marginBottom: 20 }}
        >
          <TextField
            label="Birthday set title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Shared description for birthday images"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
          <Button
            variant="contained"
            onClick={handleSaveDescription}
            disabled={uploading || loading || !recordId}
            style={{ justifySelf: "start" }}
          >
            Save Description
          </Button>

          <div>
            <input
              type="file"
              accept="image/*"
              multiple
              disabled={uploading || loading}
              onChange={(event) => {
                handleImageUpload(Array.from(event.target.files || []));
                event.target.value = "";
              }}
            />
            <div style={{ color: "#666", fontSize: 12, marginTop: 5 }}>
              {images.length} birthday image{images.length === 1 ? "" : "s"}
            </div>
            {uploading && (
              <span style={{ marginLeft: 12 }}>
                <CircularProgress size={18} />
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <div
            style={{ display: "flex", justifyContent: "center", padding: 20 }}
          >
            <CircularProgress />
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 16,
            }}
          >
            {records.length === 0 ? (
              <div style={{ color: "#666", gridColumn: "1 / -1" }}>
                No birthday image sets created yet.
              </div>
            ) : (
              records.map((birthday) => (
                <div
                  key={birthday._id}
                  style={{
                    border: `1px solid ${birthday._id === recordId ? "#1976d2" : "#ddd"}`,
                    borderRadius: 6,
                    overflow: "hidden",
                    maxWidth: 360,
                    background: "#fff",
                  }}
                >
                  {birthday.birthdayImage?.[0] ? (
                    <img
                      src={birthday.birthdayImage[0]}
                      alt={`${birthday.title || "Birthday"} cover`}
                      style={{ width: "100%", height: 180, objectFit: "cover" }}
                    />
                  ) : (
                    <div
                      style={{
                        height: 180,
                        display: "grid",
                        placeItems: "center",
                        color: "#777",
                        background: "#f5f5f5",
                      }}
                    >
                      No images
                    </div>
                  )}
                  <div style={{ padding: 10, display: "grid", gap: 8 }}>
                    <strong>{birthday.title || "Birthday"}</strong>
                    <span style={{ color: "#666", fontSize: 13 }}>
                      {birthday.description || "No description"}
                    </span>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>
                        {birthday.birthdayImage?.length || 0} image
                        {birthday.birthdayImage?.length === 1 ? "" : "s"}
                      </span>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Button
                          variant="outlined"
                          startIcon={<VisibilityOutlinedIcon />}
                          onClick={() => selectBirthdaySet(birthday)}
                          size="small"
                        >
                          Manage
                        </Button>
                        <Tooltip title="Delete birthday set">
                          <IconButton
                            aria-label={`Delete ${birthday.title || "Birthday"} set`}
                            color="error"
                            onClick={() => deleteBirthdaySet(birthday)}
                            disabled={uploading}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <Dialog
        open={viewOpen}
        onClose={() => !uploading && setViewOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span>Birthday Images ({images.length})</span>
          <Button component="label" variant="contained" disabled={uploading}>
            Add images
            <input
              hidden
              type="file"
              accept="image/*"
              multiple
              onChange={(event) => {
                handleImageUpload(Array.from(event.target.files || []));
                event.target.value = "";
              }}
            />
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {uploading && (
            <CircularProgress size={20} style={{ marginBottom: 12 }} />
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: 12,
            }}
          >
            {images.map((image, index) => (
              <div
                key={`${image}-${index}`}
                style={{ position: "relative", aspectRatio: "1 / 1" }}
              >
                <img
                  src={image}
                  alt={`Birthday ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 4,
                  }}
                />
                <Tooltip title="Remove image">
                  <IconButton
                    aria-label={`Remove birthday image ${index + 1}`}
                    onClick={() => handleDelete(index)}
                    disabled={uploading}
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      bgcolor: "white",
                      color: "error.main",
                      "&:hover": { bgcolor: "#fff0f0" },
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BirthdayImages;
